import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--yellow);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    /* background-color: var(--yellow); */

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      /* mix-blend-mode: multiply; */
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--black-navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--yellow);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              I'm Salman, a Software Engineer at <a href="https://blueyonder.com">Blue Yonder</a>. I'm also a MSCS student at <a href="https://www.gatech.edu/">Georgia Tech</a>. I completed my undergrad from Bangladesh University of
              Engineering and Technology (<a href="https://www.buet.ac.bd/">BUET</a>), majoring in Computer Science and Engineering.
            </p>

            <p>
              My university research revolved around Multimodal Machine Learning and Natural Language Processing. I work
              on Vision Language Models in the application of Cross Lingual Summarization and Visual Question Answering.
              Checkout my <a href="https://colmweb.org/">COLM'24</a> paper on the susceptibility of VLMs to optical illusions and the benchmark&nbsp;
              <a href="https://illusionvqa.github.io/">IllusionVQA</a> dataset.
              I also published papers on <a href="https://arxiv.org/abs/2407.00416">COLING'25</a>,&nbsp;
              <a href="https://www.biorxiv.org/content/10.1101/2024.09.23.614570v1.abstract">MLSB@NeurIPS'24</a> and&nbsp;
              <a href="https://arxiv.org/abs/2310.14005">ICIP'23</a>.
            </p>

            <p>
              I also extract immense pleasure in learning new tools and building new things using
              them. Throughout my undergrad life I've worked on multiple pet projects. <i>If a computer
                can do it, I can learn to make it.</i>
            </p>

            <p>
              I won multiple national and international deep learning competitions in my
              undergrad life. Through them I've grown a deep fascination for the potential of
              delivering the power of AI to the masses through software products. Professionally, I want to work
              on designing and building complex systems that can be used by millions of people.
            </p>

            <p>
              In another life, I'd have loved to become an astrophysicist. The thought that millions
              of light-years away on a planet - a cool breeze is blowing before an impending thunder
              storm, without a conscious being to experience it, is just too beautiful to not think
              about.
            </p>
          </div>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpeg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
